"use client";

import { useEffect, useRef, useState } from "react";

interface VantaBackgroundProps {
  children?: React.ReactNode;
  className?: string;
}

export function VantaBackground({ children, className }: VantaBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewportHeight, setViewportHeight] = useState<string>('100vh');
  const maxHeightRef = useRef<number>(0);

  // Handle iOS Safari dynamic viewport
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateViewportHeight = () => {
      const currentHeight = window.innerHeight;
      maxHeightRef.current = Math.max(maxHeightRef.current, currentHeight);
      setViewportHeight(`${maxHeightRef.current}px`);
    };

    maxHeightRef.current = window.innerHeight;
    updateViewportHeight();

    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', () => {
      maxHeightRef.current = window.innerHeight;
      setTimeout(updateViewportHeight, 100);
    });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight);
    }

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: viewportHeight,
        minHeight: '-webkit-fill-available',
        overflow: 'hidden',
        backgroundColor: '#1a1a1a',
      }}
    >
      {/* Video background with blur wrapper */}
      <div
        style={{
          position: 'absolute',
          top: '-20px',
          left: '-20px',
          right: '-20px',
          bottom: '-20px',
          zIndex: 0,
          filter: 'blur(4px)',
          overflow: 'hidden',
        }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          ref={(el) => { if (el) el.playbackRate = 0.65; }}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            minWidth: 'calc(100% + 40px)',
            minHeight: 'calc(100% + 40px)',
            width: 'auto',
            height: 'auto',
            transform: 'translate(-50%, -50%)',
            objectFit: 'cover',
          }}
        >
          <source src="/auth-bg.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Dark tint overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 1,
        }}
      />

      {/* Content layer */}
      <div
        className={className}
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          width: '100%',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {children}
      </div>
    </div>
  );
}
